// Copyright 2019 Drivetrain Hub LLC
// Helper functions for three.js.

// eslint-disable-next-line no-unused-vars
import {Shape, BufferGeometry, ExtrudeBufferGeometry} from "three";
import gear from "../gear";

/**
 * Calculate extrusion settings used for sweeping shape with a helical twist.
 * @param {number} length - Length of extrusion.
 * @param {number} helixRadius - Helix radius at the specified angle.
 * @param {number} helixAngle - Helix angle at the specified radius.
 * @return {{b: number, extrudeSettings: {depth: *, steps: number, bevelEnabled: boolean}}}
 */
function helixSettings(length, helixRadius, helixAngle) {

  // HELIX PARAMS
  const {phi, b} = gear.helixRotation(length, helixAngle, helixRadius);

  // EXTRUDE SETTINGS
  // steps defines the depth discretization
  // 20 steps / 180 degrees of twist gives smooth sweep
  const steps = Math.ceil(20 * Math.abs(phi) / Math.PI);
  const extrudeSettings = {
    depth: length,
    steps: steps,
    curveSegments: 12,  // discretized segments of each curve in cross section shape; default=12
    bevelEnabled: false,
  };

  // DEV ONLY
  // console.log('extrudeSettings', extrudeSettings);

  return {b, extrudeSettings}
}

/**
 * Extrude shape with a helical twist along its z-axis.
 * @param {Shape} shape - THREE.Shape object.
 * @param {number} length - Length of extrusion.
 * @param {number} helixRadius - Helix radius at the specified angle.
 * @param {number} helixAngle - Helix angle at the specified radius.
 * @param {number} diameterCylinder - Diameter of cylindrical surface to apply smoothing of vertex normals.
 * @return {Object} THREE.ExtrudeBufferGeometry object.
 */
export function sweepBufferGeometryHelix(shape, length, helixRadius, helixAngle, diameterCylinder = 0) {

  const {b, extrudeSettings} = helixSettings(length, helixRadius, helixAngle);
  let geometry = new ExtrudeBufferGeometry(shape, extrudeSettings);

  // cylinder smoothing settings
  const radiusCylinder = diameterCylinder / 2;
  let signCylinderNormal = 1;
  if (diameterCylinder < 2 * helixRadius) signCylinderNormal = -1;

  // helical transform of vertices in xy-plane
  const posAttr = geometry.getAttribute('position');
  const normAttr = geometry.getAttribute('normal');

  let x, y, z, r;
  let nx, ny, nr;
  let phi, phiStart, phiEnd, phiDelta;

  for (let i = 0; i < posAttr.array.length / 3; i++) {
    // region Position Transform

    // get vertex position
    x = posAttr.getX(i);
    y = posAttr.getY(i);
    z = posAttr.getZ(i);

    // calculate transform params
    phiDelta = z / b;
    r = Math.hypot(x, y);
    phiStart = Math.atan2(y, x);
    phiEnd = phiStart + phiDelta;

    // calculate and assign new position of vertex
    x += r * Math.cos(phiEnd) - r * Math.cos(phiStart);
    y += r * Math.sin(phiEnd) - r * Math.sin(phiStart);
    posAttr.setXY(i, x, y);

    // endregion

    // region Normal Transform

    // NOTES: Normals algorithm
    //  1. xy-components of each normal in a given xy-plane should be *transformed* identically.
    //      a. Note however, the normals themselves are not necessarily the same (they are for prism example).
    //      b. The normal vector xy-components should be transformed identical to the position vector components.
    //          i. Except the vector magnitude and direction shall be taken from the normal vector instead.
    //  2. z-component of each normal is assumed unchanged, which may not true to reality, but results are satisfactory.

    nx = normAttr.getX(i);
    ny = normAttr.getY(i);

    nr = Math.hypot(nx, ny);

    if (r !== 0 && r > radiusCylinder - 0.0001 && r < radiusCylinder + 0.0001) {
      // create smoothing normals by directing towards/away from (0, 0)
      phi = Math.atan2(y, x);

      nx = nr * Math.cos(phi) * signCylinderNormal;
      ny = nr * Math.sin(phi) * signCylinderNormal;
    } else {
      phiStart = Math.atan2(ny, nx);
      phiEnd = phiStart + phiDelta;

      nx += nr * Math.cos(phiEnd) - nr * Math.cos(phiStart);
      ny += nr * Math.sin(phiEnd) - nr * Math.sin(phiStart);
    }
    normAttr.setXY(i, nx, ny);

    // endregion
  }

  // None of these are needed in this case:
  // geometry.getAttribute('position').needsUpdate = true;
  // geometry.getAttribute('normal').needsUpdate = true;
  // geometry.computeVertexNormals();
  // geometry.normalizeNormals();
  // geometry.normalize();

  return geometry;
}

/**
 * Compute and set vertex normals for smooth shading along a cylindrical surface.
 * @param {BufferGeometry} geometry - THREE.BufferGeometry object.
 * @param {number} diameterReference - Reference diameter, used to determine if cylinder surface is a bore.
 * @param {number} diameterCylinder - Diameter of cylindrical surface to set smooth shading.
 */
export function computeBufferCylinderNormals(geometry, diameterReference, diameterCylinder) {

  // cylinder smoothing settings

  const radiusCylinder = diameterCylinder / 2;
  let signCylinderNormal = 1;
  if (diameterCylinder < diameterReference) signCylinderNormal = -1;  // hole

  // BufferGeometry vertices

  const posAttr = geometry.getAttribute('position');
  const normAttr = geometry.getAttribute('normal');

  let x, y, r, phi;
  let nx, ny, nr;

  for (let i = 0; i < posAttr.array.length / 3; i++) {

    // get vertex position

    x = posAttr.getX(i);
    y = posAttr.getY(i);
    r = Math.hypot(x, y);

    // get vertex normal

    nx = normAttr.getX(i);
    ny = normAttr.getY(i);
    nr = Math.hypot(nx, ny);

    // transform normal for vertices on cylinder surface

    if (r !== 0 && r > radiusCylinder - 0.0001 && r < radiusCylinder + 0.0001) {
      // create smoothing normals by directing towards/away from (0, 0)
      phi = Math.atan2(y, x);

      nx = nr * Math.cos(phi) * signCylinderNormal;
      ny = nr * Math.sin(phi) * signCylinderNormal;

      normAttr.setXY(i, nx, ny);
    }
  }
}
